import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import queryString from "query-string";

import Container from "@material-ui/core/Container";
import {
  etsyRequestAccessToken,
  etsyFetchTransactions,
} from "src/apiService/modules/etsy";
import { toast } from "react-toastify";
import { useRecordEvent } from "src/hooks/useRecordEvent";
import {
  completeUserSetupQuestionCardPlatform,
  doesUserHaveCardPlatform,
} from "src/apiService/modules/users";
import type MonthYear from "src/interfaces/monthYear.interface";

interface EtsyQueryResponse {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
  error_uri?: string;
}

export const EtsyAuthCallbackPage = () => {
  const recordEvent = useRecordEvent();

  const history = useHistory();
  const location = useLocation();
  const search = location.search;

  const requestAccessToken = async (code, state) => {
    return await etsyRequestAccessToken({
      authorizationCode: code,
      state: state,
    });
  };

  // Guards against exchanging the same callback twice. Holds the in-flight
  // request rather than a spent flag, so a remounted effect joins the original
  // exchange instead of short-circuiting into a page that never resolves.
  const exchangeRef = React.useRef<{
    code: string;
    promise: ReturnType<typeof requestAccessToken>;
  } | null>(null);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      const query: EtsyQueryResponse = queryString.parse(search);
      if (query.code && query.state) {
        if (exchangeRef.current?.code !== query.code) {
          exchangeRef.current = {
            code: query.code,
            promise: requestAccessToken(query.code, query.state),
          };
        }
        try {
          const resp = await exchangeRef.current.promise;
          if (cancel) return;
          if (resp.data.success) {
            // A replayed callback (reload / back-navigation) resolves as
            // alreadyCompleted: the first exchange connected the account and ran
            // the initial import, so only the redirect is left to do.
            if (resp.data.alreadyCompleted) {
              history.push(resp.data.back || "/integrations");
              return;
            }
            toast.success("Etsy account connected!");
            recordEvent({
              type: "feature-adoption",
              feature: "integration-added",
              element: "sale-platform-etsy",
            });
            if (await doesUserHaveCardPlatform("Etsy")) {
              await completeUserSetupQuestionCardPlatform("Etsy");
              // The connection already succeeded by this point. Isolate the
              // initial import so a failed/blocked import (e.g. non-ultimate
              // reconnect hitting "can't perform a direct import") doesn't
              // surface as an auth error or strand the user on this page.
              try {
                const currentDate = new Date();
                const start = {
                  month: 1,
                  year: currentDate.getFullYear() - 1,
                } as MonthYear;
                const end = {
                  month: currentDate.getMonth() + 1,
                  year: currentDate.getFullYear(),
                } as MonthYear;
                toast.success("Etsy sale sync started!");
                await etsyFetchTransactions(start, end);
              } catch (importError) {
                console.error("etsy initial import failed", importError);
              }
            }
            if (resp.data.back) {
              history.push(resp.data.back);
            } else {
              history.push("/integrations");
            }
          }
        } catch (e) {
          if (cancel) return;
          console.log(e);
          toast.error(
            `There was an error authenticating with Etsy: ${(
              e as Error
            ).toString()}. Please try again.`
          );
          // This page is nothing but a spinner, so every path has to leave it.
          history.push("/integrations");
        }
      } else if (query.error_description) {
        toast.error(
          `There was an error authenticating with Etsy: ${query.error_description}. Please try again.`
        );
        history.push("/integrations");
      } else {
        toast.error(`Unknown error response from Etsy. Please try again.`);
        history.push("/integrations");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [history, search]);

  return (
    <Container component="main" maxWidth="xs">
      <Box className="flex justify-center items-center h-screen">
        <CircularProgress size={50} />
      </Box>
    </Container>
  );
};

export default EtsyAuthCallbackPage;
